import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import Select from "react-select";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { base_url } from "../../constants";
import "./addquestion.css";

const AddQuestions = () => {
  const location = useLocation();
  const bankData = location?.state?.bankdata || null;
  const currentBankId = location?.state?.bank_id || bankData?.bank_id || "0";
  const bankTitle = bankData?.bank_name || (currentBankId !== "0" ? `Question Bank #${currentBankId}` : "Questions Pool");

  document.title = `${bankTitle} | Medicotoon`;

  const localdata = localStorage.getItem("elmatary_admin");
  const adminData = localdata ? JSON.parse(localdata) : {};

  // Form states for creating a new question (unlinked by default)
  const [questionText, setQuestionText] = useState("");
  const [questionImage, setQuestionImage] = useState("");
  const [explain, setExplain] = useState("");
  const [helpText, setHelpText] = useState("");
  const [helpPdf, setHelpPdf] = useState("");
  const [helpVideo, setHelpVideo] = useState("");
  const [answersList, setAnswersList] = useState([
    { id: 1, answer: "", answer_exp: "", checked: true },
    { id: 2, answer: "", answer_exp: "", checked: false },
  ]);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Questions List & Server Pagination States
  const [unassignedQuestions, setUnassignedQuestions] = useState([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [paginationInfo, setPaginationInfo] = useState({
    total_records: 0,
    total_pages: 1,
    current_page: 1,
    limit: 10,
  });

  // Assign Modal States
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [questionToAssign, setQuestionToAssign] = useState(null);
  const [courses, setCourses] = useState([]);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [units, setUnits] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);

  // Edit Modal States
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editQuestionData, setEditQuestionData] = useState({});
  const [editAnswersList, setEditAnswersList] = useState([]);
  const [uploadingEditImg, setUploadingEditImg] = useState(false);
  const [submittingEdit, setSubmittingEdit] = useState(false);

  // Fetch questions (by bank_id if provided, else unassigned) with Server-side Pagination & Search
  const getUnassignedQuestions = async (page = currentPage, limit = pageSize, search = searchTerm) => {
    setLoadingQuestions(true);
    try {
      const data_send = {
        bank_id: currentBankId,
        unit_id: bankData?.unit_id || "0",
        course_id: bankData?.course_id || "0",
        page: page,
        limit: limit,
        search: search,
      };
      const res = await axios.post(
        `${base_url}/admin/Exams/select_questions.php`,
        JSON.stringify(data_send)
      );

      if (res && res.message && Array.isArray(res.message)) {
        setUnassignedQuestions(res.message);
        if (res.pagination) {
          setPaginationInfo(res.pagination);
        } else {
          setPaginationInfo({
            total_records: res.message.length,
            total_pages: Math.ceil(res.message.length / limit) || 1,
            current_page: page,
            limit: limit,
          });
        }
      } else {
        setUnassignedQuestions([]);
        setPaginationInfo({
          total_records: 0,
          total_pages: 1,
          current_page: 1,
          limit: limit,
        });
      }
    } catch (err) {
      console.error("Error fetching questions", err);
      toast.error("Failed to load questions");
      setUnassignedQuestions([]);
    } finally {
      setLoadingQuestions(false);
    }
  };

  // Fetch Courses list for assignment
  const getCourses = async () => {
    setLoadingCourses(true);
    try {
      const data_send = {
        admin_id: adminData.admin_id,
        access_token: adminData.access_token,
      };
      const res = await axios.post(
        `${base_url}/admin/courses/select_courses.php`,
        JSON.stringify(data_send)
      );
      if (res.status === "success" && Array.isArray(res.message)) {
        const courseOptions = res.message.map((c) => ({
          value: c.course_id,
          label: c.course_name,
        }));
        setCourses(courseOptions);
      }
    } catch (err) {
      console.error("Error fetching courses", err);
    } finally {
      setLoadingCourses(false);
    }
  };

  // Fetch Units for a selected course
  const getUnits = async (courseId) => {
    if (!courseId) {
      setUnits([]);
      setSelectedUnit(null);
      return;
    }
    setLoadingUnits(true);
    setUnits([]);
    setSelectedUnit(null);
    try {
      const send_data = {
        admin_id: adminData.admin_id,
        access_token: adminData.access_token,
        course_id: courseId,
      };
      const res = await axios.post(
        `${base_url}/admin/courses/select_course_units.php`,
        send_data
      );
      if (res.status === "success" && Array.isArray(res.message)) {
        setUnits(
          res.message.map((u) => ({
            value: u.unit_id,
            label: u.unit_name,
          }))
        );
      }
    } catch (err) {
      console.error("Error fetching units", err);
      toast.error("Failed to load units");
    } finally {
      setLoadingUnits(false);
    }
  };

  useEffect(() => {
    getUnassignedQuestions(1, pageSize, searchTerm);
    getCourses();
  }, [currentBankId]);

  // Search debounce
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setCurrentPage(1);
      getUnassignedQuestions(1, pageSize, searchTerm);
    }, 400);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= (paginationInfo.total_pages || 1)) {
      setCurrentPage(newPage);
      getUnassignedQuestions(newPage, pageSize, searchTerm);
    }
  };

  const handlePageSizeChange = (e) => {
    const newSize = Number(e.target.value);
    setPageSize(newSize);
    setCurrentPage(1);
    getUnassignedQuestions(1, newSize, searchTerm);
  };

  // Upload Image Handler
  const handleUploadImage = async (file, isEdit = false) => {
    if (!file) return;
    const formdata = new FormData();
    formdata.append("image", file);
    if (isEdit) setUploadingEditImg(true);
    else setUploadingImg(true);

    try {
      const res = await axios.post(
        `${base_url}/admin/image_uploader.php`,
        formdata
      );
      if (res) {
        if (isEdit) {
          setEditQuestionData((prev) => ({
            ...prev,
            question_image_url: res,
          }));
        } else {
          setQuestionImage(res);
        }
        toast.success("Image uploaded successfully");
      }
    } catch (err) {
      console.error("Error uploading image", err);
      toast.error("Error uploading image");
    } finally {
      if (isEdit) setUploadingEditImg(false);
      else setUploadingImg(false);
    }
  };

  // Answers handlers for adding
  const handleAnswerChange = (index, field, value) => {
    const list = [...answersList];
    list[index][field] = value;
    setAnswersList(list);
  };

  const handleSelectCorrect = (index) => {
    const list = answersList.map((item, idx) => ({
      ...item,
      checked: idx === index,
    }));
    setAnswersList(list);
  };

  const handleAddOption = () => {
    setAnswersList([
      ...answersList,
      {
        id: answersList.length + 1,
        answer: "",
        answer_exp: "",
        checked: false,
      },
    ]);
  };

  const handleRemoveOption = (index) => {
    if (answersList.length <= 2) {
      toast.warn("At least 2 answer options are required");
      return;
    }
    const list = answersList.filter((_, i) => i !== index);
    if (answersList[index].checked && list.length > 0) {
      list[0].checked = true;
    }
    setAnswersList(list);
  };

  // Submit Add Question
  const handleCreateQuestion = async (e) => {
    e.preventDefault();
    if (!questionText.trim()) {
      toast.warn("Please enter question title");
      return;
    }
    const validChecked = answersList.find((a) => a.checked);
    if (!validChecked || !validChecked.answer.trim()) {
      toast.warn("Please select and write the valid correct answer");
      return;
    }

    setIsSubmitting(true);
    try {
      const data_send = {
        bank_id: currentBankId,
        unit_id: bankData?.unit_id || "0",
        course_id: bankData?.course_id || "0",
        exam_id: "0",
        question_text: questionText,
        question_answers: answersList
          ?.map((item) => `${item?.answer}/**exp**/${item?.answer_exp || ""}`)
          ?.join("//CAMP//"),
        question_valid_answer: validChecked.answer,
        question_image: questionImage || null,
        help_text: helpText || "",
        help_pdf: helpPdf || "",
        help_video: helpVideo || "",
        pdf_page: "",
        video_time: "",
        explain: explain || "",
      };

      const res = await axios.post(
        `${base_url}/admin/Exams/add_ques.php`,
        JSON.stringify(data_send)
      );

      if (res.status === "success") {
        toast.success(currentBankId !== "0" ? "Question added to Bank successfully" : "Question created successfully (Unassigned)");
        setQuestionText("");
        setQuestionImage("");
        setExplain("");
        setHelpText("");
        setHelpPdf("");
        setHelpVideo("");
        setAnswersList([
          { id: 1, answer: "", answer_exp: "", checked: true },
          { id: 2, answer: "", answer_exp: "", checked: false },
        ]);
        getUnassignedQuestions(1, pageSize, searchTerm);
      } else {
        toast.error(res.message || "Failed to add question");
      }
    } catch (err) {
      console.error("Error creating question", err);
      toast.error("Server connection error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Assign Question to Course & Unit
  const handleOpenAssignModal = (question) => {
    setQuestionToAssign(question);
    setSelectedCourse(null);
    setSelectedUnit(null);
    setUnits([]);
    setIsAssignModalOpen(true);
  };

  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    if (!selectedCourse || !selectedUnit) {
      toast.warn("Please select both course and unit");
      return;
    }

    setIsAssigning(true);
    try {
      const validChecked = (questionToAssign.question_answers || []).find(
        (a) => a.answer_check
      );

      // We assign by creating a linked copy with referenced_from pointing to original question_id
      const data_send = {
        question_id: questionToAssign.question_id,
        referenced_from: questionToAssign.referenced_from || questionToAssign.question_id,
        unit_id: selectedUnit.value,
        course_id: selectedCourse.value,
        exam_id: "0",
        question_text: questionToAssign.question_text,
        question_answers: (questionToAssign.question_answers || [])
          .map((item) => `${item?.answer_text}/**exp**/${item?.answer_exp || ""}`)
          .join("//CAMP//"),
        question_valid_answer: validChecked
          ? validChecked.answer_text
          : questionToAssign.question_valid_answer || "",
        question_image: questionToAssign.question_image || null,
        help_text: questionToAssign.help_text || "",
        help_pdf: questionToAssign.help_pdf || "",
        help_video: questionToAssign.help_video || "",
        pdf_page: questionToAssign.pdf_page || "",
        video_time: questionToAssign.video_time || "",
        explain: questionToAssign.explain || "",
      };

      const res = await axios.post(
        `${base_url}/admin/Exams/add_ques.php`,
        JSON.stringify(data_send)
      );

      if (res.status === "success") {
        toast.success(`Question successfully assigned to ${selectedUnit.label}!`);
        setIsAssignModalOpen(false);
        setQuestionToAssign(null);
        getUnassignedQuestions(currentPage, pageSize, searchTerm);
      } else {
        toast.error(res.message || "Failed to assign question");
      }
    } catch (err) {
      console.error("Error assigning question", err);
      toast.error("Server connection error");
    } finally {
      setIsAssigning(false);
    }
  };

  // Edit Question Handlers
  const handleOpenEdit = (item) => {
    setEditQuestionData({
      ...item,
      question_image_url: item.question_image || "",
    });
    const parsedAnswers = (item.question_answers || []).map((ans, idx) => ({
      id: idx + 1,
      answer: ans.answer_text || "",
      answer_exp: ans.answer_exp || "",
      checked: !!ans.answer_check,
    }));
    setEditAnswersList(
      parsedAnswers.length
        ? parsedAnswers
        : [
            { id: 1, answer: "", answer_exp: "", checked: true },
            { id: 2, answer: "", answer_exp: "", checked: false },
          ]
    );
    setIsEditModalOpen(true);
  };

  const handleEditQuestionSubmit = async (e) => {
    e.preventDefault();
    const validChecked = editAnswersList.find((a) => a.checked);
    if (!validChecked || !validChecked.answer.trim()) {
      toast.warn("Please select the valid correct answer");
      return;
    }

    setSubmittingEdit(true);
    try {
      const data_send = {
        question_id: editQuestionData.question_id,
        unit_id: editQuestionData.unit_id || "0",
        course_id: editQuestionData.course_id || "0",
        exam_id: editQuestionData.exam_id || "0",
        question_text: editQuestionData.question_text,
        question_answers: editAnswersList
          ?.map((item) => `${item?.answer}/**exp**/${item?.answer_exp || ""}`)
          ?.join("//CAMP//"),
        question_valid_answer: validChecked.answer,
        question_image: editQuestionData.question_image_url || "",
        help_text: editQuestionData.help_text || "",
        help_pdf: editQuestionData.help_pdf || "",
        help_video: editQuestionData.help_video || "",
        pdf_page: editQuestionData.pdf_page || "",
        video_time: editQuestionData.video_time || "",
        explain: editQuestionData.explain || "",
      };

      const res = await axios.post(
        `${base_url}/admin/Exams/edit_ques.php`,
        JSON.stringify(data_send)
      );

      if (res.status === "success") {
        toast.success("Question updated successfully");
        setIsEditModalOpen(false);
        getUnassignedQuestions(currentPage, pageSize, searchTerm);
      } else {
        toast.error(res.message || "Failed to update question");
      }
    } catch (err) {
      console.error("Error editing question", err);
      toast.error("Server connection error");
    } finally {
      setSubmittingEdit(false);
    }
  };

  // Bulk Delete States for unassigned questions table
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);
  const [isDeletingBulk, setIsDeletingBulk] = useState(false);

  // Toggle single question selection
  const toggleSelectQuestion = (qId) => {
    setSelectedQuestionIds((prev) =>
      prev.includes(qId) ? prev.filter((id) => id !== qId) : [...prev, qId]
    );
  };

  // Toggle select all on current page
  const toggleSelectAllPage = () => {
    if (selectedQuestionIds.length === unassignedQuestions.length && unassignedQuestions.length > 0) {
      setSelectedQuestionIds([]);
    } else {
      setSelectedQuestionIds(unassignedQuestions.map((q) => q.question_id));
    }
  };

  // Bulk Delete Handler
  const handleBulkDelete = async () => {
    if (selectedQuestionIds.length === 0) {
      toast.warn("Please select at least one question to delete");
      return;
    }

    if (
      window.confirm(
        `Are you sure you want to delete ${selectedQuestionIds.length} question(s)?`
      )
    ) {
      setIsDeletingBulk(true);
      try {
        const promises = selectedQuestionIds.map((id) =>
          axios.post(`${base_url}/admin/Exams/delete_question.php`, {
            question_id: id,
          })
        );
        await Promise.all(promises);
        toast.success(`Successfully deleted ${selectedQuestionIds.length} question(s)`);
        setSelectedQuestionIds([]);
        getUnassignedQuestions(currentPage, pageSize, searchTerm);
      } catch (err) {
        console.error("Error bulk deleting questions", err);
        toast.error("An error occurred during bulk deletion");
      } finally {
        setIsDeletingBulk(false);
      }
    }
  };

  // Delete Question Handler
  const handleDeleteQuestion = async (questionId) => {
    if (window.confirm("Are you sure you want to delete this question?")) {
      try {
        await axios.post(`${base_url}/admin/Exams/delete_question.php`, {
          question_id: questionId,
        });
        toast.success("Question deleted successfully");
        setSelectedQuestionIds((prev) => prev.filter((id) => id !== questionId));
        getUnassignedQuestions(currentPage, pageSize, searchTerm);
      } catch (err) {
        console.error("Error deleting question", err);
        toast.error("Failed to delete question");
      }
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid={true}>
          <Breadcrumbs
            title="Question Bank"
            breadcrumbItem={currentBankId !== "0" ? `${bankTitle} - Questions` : "Add & Manage Standalone Questions"}
          />

          {/* Form Card: Add New Standalone Question */}
          <Row>
            <Col lg={12}>
              <Card className="shadow-sm mb-4">
                <div className="p-4 border-bottom bg-light bg-opacity-25">
                  <div className="d-flex align-items-center">
                    <div className="flex-shrink-0 me-3">
                      <div className="avatar-sm">
                        <div className="avatar-title rounded-circle bg-soft-primary text-primary font-size-18">
                          <i className="bx bx-plus-circle"></i>
                        </div>
                      </div>
                    </div>
                    <div className="flex-grow-1">
                      <h5 className="font-size-16 mb-1 text-primary">
                        {currentBankId !== "0" ? `Add Question to ${bankTitle}` : "Add New Standalone Question"}
                      </h5>
                      <p className="text-muted mb-0">
                        {currentBankId !== "0" 
                          ? `Add questions directly to "${bankTitle}". You can also assign questions from here to any unit.`
                          : "Create questions freely without attaching them to any course or unit right away. You can assign them to any course unit later."}
                      </p>
                    </div>
                  </div>
                </div>

                <CardBody className="p-4">
                  <form onSubmit={handleCreateQuestion}>
                    {/* Question Title */}
                    <div className="mb-3">
                      <label className="form-label fw-bold">
                        Question Title <span className="text-danger">*</span>
                      </label>
                      <textarea
                        className="form-control"
                        rows="3"
                        placeholder="Enter question text / title..."
                        value={questionText}
                        onChange={(e) => setQuestionText(e.target.value)}
                        required
                      ></textarea>
                    </div>

                    {/* Question Image */}
                    <div className="mb-3">
                      <label className="form-label fw-bold">
                        Question Image (Optional)
                      </label>
                      <div className="d-flex gap-2 align-items-center">
                        <input
                          type="file"
                          className="form-control"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) handleUploadImage(file, false);
                          }}
                        />
                        {uploadingImg && (
                          <Spinner size="sm" color="primary" />
                        )}
                      </div>
                      {questionImage && (
                        <div className="mt-2 d-flex align-items-center gap-2">
                          <img
                            src={questionImage}
                            alt="preview"
                            style={{
                              maxHeight: "120px",
                              borderRadius: "6px",
                              border: "1px solid #ddd",
                            }}
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => setQuestionImage("")}
                          >
                            Remove Image
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Answer Options */}
                    <div className="mb-4 p-3 bg-light rounded border">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <div>
                          <label className="form-label fw-bold mb-0">
                            Answer Options (Mark the radio for the correct answer) <span className="text-danger">*</span>
                          </label>
                          <small className="d-block text-muted">
                            Add choices and select which one is the correct answer.
                          </small>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary"
                          onClick={handleAddOption}
                        >
                          <i className="bx bx-plus me-1"></i> Add Option
                        </button>
                      </div>

                      {answersList.map((item, index) => (
                        <div
                          key={index}
                          className="p-2 mb-2 bg-white rounded border d-flex flex-column gap-2"
                        >
                          <div className="d-flex align-items-center gap-2">
                            <input
                              type="radio"
                              name="correct_answer_add"
                              checked={item.checked}
                              onChange={() => handleSelectCorrect(index)}
                              style={{
                                cursor: "pointer",
                                transform: "scale(1.2)",
                              }}
                              title="Mark as correct answer"
                            />
                            <Input
                              type="text"
                              placeholder={`Answer Option ${index + 1}`}
                              value={item.answer}
                              onChange={(e) =>
                                handleAnswerChange(
                                  index,
                                  "answer",
                                  e.target.value
                                )
                              }
                              required
                            />
                            {answersList.length > 2 && (
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleRemoveOption(index)}
                              >
                                <i className="bx bx-trash"></i>
                              </button>
                            )}
                          </div>
                          <Input
                            type="text"
                            placeholder={`Explanation for answer ${index + 1} (Optional)`}
                            value={item.answer_exp || ""}
                            onChange={(e) =>
                              handleAnswerChange(
                                index,
                                "answer_exp",
                                e.target.value
                              )
                            }
                            className="font-size-12 text-muted"
                          />
                        </div>
                      ))}
                    </div>

                    {/* General Explanation */}
                    <div className="mb-3">
                      <label className="form-label fw-bold">
                        General Explanation (Optional)
                      </label>
                      <textarea
                        className="form-control"
                        rows="2"
                        placeholder="Enter general explanation for the question / correct answer..."
                        value={explain}
                        onChange={(e) => setExplain(e.target.value)}
                      ></textarea>
                    </div>

                    {/* Submit Button */}
                    <div className="d-flex justify-content-end">
                      <button
                        type="submit"
                        className="btn btn-success px-4 d-flex align-items-center gap-2"
                        disabled={isSubmitting || uploadingImg}
                      >
                        {isSubmitting ? (
                          <>
                            <Spinner size="sm" />
                            <span>Saving Question...</span>
                          </>
                        ) : (
                          <>
                            <i className="bx bx-save font-size-16"></i>
                            <span>{currentBankId !== "0" ? "Save Question to Bank" : "Save Question (Unassigned)"}</span>
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Unassigned Questions List & Assignment Table */}
          <Row>
            <Col lg={12}>
              <Card className="shadow-sm">
                <CardBody>
                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                    <div>
                      <h5 className="card-title font-size-16 mb-0 text-primary">
                        <i className="bx bx-list-ul me-1"></i> {currentBankId !== "0" ? `${bankTitle} Questions` : "Unassigned Questions Pool"}
                      </h5>
                      <small className="text-muted">
                        Total Questions: <b>{paginationInfo.total_records || unassignedQuestions.length}</b>
                      </small>
                    </div>
                    <div className="d-flex gap-2 align-items-center">
                      <div className="position-relative" style={{ minWidth: "280px" }}>
                        <Input
                          type="text"
                          className="form-control form-control-sm pe-4"
                          placeholder="Search unassigned questions..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <i
                          className="bx bx-search-alt position-absolute"
                          style={{
                            right: "10px",
                            top: "50%",
                            transform: "translateY(-50%)",
                            color: "#74788d",
                          }}
                        ></i>
                      </div>
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        onClick={() => getUnassignedQuestions(currentPage, pageSize, searchTerm)}
                        title="Refresh Unassigned Questions"
                      >
                        <i className="bx bx-refresh me-1"></i> Refresh
                      </button>
                    </div>
                  </div>

                  {/* Top controls: Page Size Selector & Bulk Delete */}
                  <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                    <div className="d-flex align-items-center gap-2">
                      <span className="text-muted font-size-13">Show:</span>
                      <select
                        className="form-select form-select-sm"
                        style={{ width: "80px" }}
                        value={pageSize}
                        onChange={handlePageSizeChange}
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={30}>30</option>
                        <option value={50}>50</option>
                      </select>
                      <span className="text-muted font-size-13">entries</span>
                    </div>

                    {/* Bulk Delete Button when questions selected */}
                    {selectedQuestionIds.length > 0 && (
                      <div className="d-flex align-items-center gap-2">
                        <span className="badge bg-danger-subtle text-danger font-size-13 p-2">
                          {selectedQuestionIds.length} question(s) selected
                        </span>
                        <button
                          type="button"
                          className="btn btn-sm btn-danger d-flex align-items-center gap-1 shadow-sm"
                          onClick={handleBulkDelete}
                          disabled={isDeletingBulk}
                        >
                          {isDeletingBulk ? (
                            <Spinner size="sm" />
                          ) : (
                            <i className="bx bx-trash font-size-15"></i>
                          )}
                          Delete Selected ({selectedQuestionIds.length})
                        </button>
                      </div>
                    )}
                  </div>

                  {loadingQuestions ? (
                    <div className="text-center py-5">
                      <Spinner color="primary" />
                      <p className="mt-2 text-muted">Loading questions...</p>
                    </div>
                  ) : unassignedQuestions.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <i className="bx bx-check-circle font-size-40 mb-2 d-block text-success"></i>
                      <p className="font-size-16 mb-1">
                        No unassigned questions found.
                      </p>
                      <small>
                        All questions are currently assigned to units or you can create a new one using the form above.
                      </small>
                    </div>
                  ) : (
                    <>
                      <div className="table-responsive">
                        <table className="table table-bordered table-hover align-middle table-nowrap mb-0">
                          <thead className="table-light">
                            <tr>
                              <th style={{ width: "40px" }} className="text-center">
                                <input
                                  type="checkbox"
                                  className="form-check-input cursor-pointer"
                                  checked={
                                    unassignedQuestions.length > 0 &&
                                    selectedQuestionIds.length === unassignedQuestions.length
                                  }
                                  onChange={toggleSelectAllPage}
                                  title="Select/Deselect All on Page"
                                />
                              </th>
                              <th style={{ width: "50px" }}>#</th>
                              <th style={{ width: "120px" }}>Question ID</th>
                              <th>Question Title</th>
                              <th style={{ width: "100px" }}>Image</th>
                              <th>Answers</th>
                              <th style={{ width: "160px" }}>Assignment</th>
                              <th style={{ width: "150px" }}>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {unassignedQuestions.map((item, idx) => {
                              const rowNumber = (currentPage - 1) * pageSize + idx + 1;
                              const answers = item.question_answers || [];
                              const isSelected = selectedQuestionIds.includes(item.question_id);
                              return (
                                <tr
                                  key={item.question_id || idx}
                                  className={isSelected ? "table-active" : ""}
                                >
                                  <td className="text-center">
                                    <input
                                      type="checkbox"
                                      className="form-check-input cursor-pointer"
                                      checked={isSelected}
                                      onChange={() => toggleSelectQuestion(item.question_id)}
                                    />
                                  </td>
                                  <td><b>{rowNumber}</b></td>
                                  <td>{item.question_id}</td>
                                  <td>
                                    <div
                                      style={{
                                        maxWidth: "320px",
                                        whiteSpace: "normal",
                                        wordBreak: "break-word",
                                      }}
                                    >
                                      {item.question_text}
                                    </div>
                                  </td>
                                  <td>
                                    {item.question_image ? (
                                      <img
                                        src={item.question_image}
                                        alt="question"
                                        style={{
                                          width: "55px",
                                          height: "55px",
                                          objectFit: "cover",
                                          borderRadius: "6px",
                                          border: "1px solid #eee",
                                        }}
                                      />
                                    ) : (
                                      <span className="text-muted font-size-12">No Image</span>
                                    )}
                                  </td>
                                  <td>
                                    <ul
                                      style={{
                                        listStyleType: "none",
                                        padding: 0,
                                        margin: 0,
                                        textAlign: "left",
                                      }}
                                    >
                                      {answers.map((ans, aIdx) => (
                                        <li
                                          key={aIdx}
                                          style={{
                                            color: ans.answer_check ? "#28a745" : "#dc3545",
                                            fontWeight: ans.answer_check ? "600" : "normal",
                                            marginBottom: "3px",
                                          }}
                                        >
                                          {ans.answer_check ? "✓ " : "• "}
                                          {ans.answer_text}
                                        </li>
                                      ))}
                                    </ul>
                                  </td>
                                  <td>
                                    <button
                                      className="btn btn-sm btn-success d-flex align-items-center gap-1 shadow-sm"
                                      onClick={() => handleOpenAssignModal(item)}
                                      title="Assign this question to a Unit"
                                    >
                                      <i className="bx bx-check-square"></i>
                                      <span>Assign to Unit</span>
                                    </button>
                                  </td>
                                  <td>
                                    <div className="d-flex gap-2">
                                      <button
                                        className="btn btn-sm btn-primary"
                                        onClick={() => handleOpenEdit(item)}
                                        title="Edit Question"
                                      >
                                        <i className="bx bx-edit-alt me-1"></i> Edit
                                      </button>
                                      <button
                                        className="btn btn-sm btn-danger"
                                        onClick={() => handleDeleteQuestion(item.question_id)}
                                        title="Delete Question"
                                      >
                                        <i className="bx bx-trash me-1"></i> Delete
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Server Pagination Controls */}
                      <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                        <div className="text-muted font-size-13">
                          Showing {(currentPage - 1) * pageSize + 1} to{" "}
                          {Math.min(currentPage * pageSize, paginationInfo.total_records || unassignedQuestions.length)}{" "}
                          of {paginationInfo.total_records || unassignedQuestions.length} entries
                        </div>
                        {paginationInfo.total_pages > 1 && (
                          <Pagination className="mb-0">
                            <PaginationItem disabled={currentPage <= 1}>
                              <PaginationLink
                                first
                                onClick={() => handlePageChange(1)}
                              />
                            </PaginationItem>
                            <PaginationItem disabled={currentPage <= 1}>
                              <PaginationLink
                                previous
                                onClick={() => handlePageChange(currentPage - 1)}
                              />
                            </PaginationItem>

                            {[...Array(paginationInfo.total_pages)].map((_, i) => {
                              const pageNum = i + 1;
                              if (
                                pageNum === 1 ||
                                pageNum === paginationInfo.total_pages ||
                                (pageNum >= currentPage - 2 && pageNum <= currentPage + 2)
                              ) {
                                return (
                                  <PaginationItem
                                    active={pageNum === currentPage}
                                    key={pageNum}
                                  >
                                    <PaginationLink
                                      onClick={() => handlePageChange(pageNum)}
                                    >
                                      {pageNum}
                                    </PaginationLink>
                                  </PaginationItem>
                                );
                              }
                              return null;
                            })}

                            <PaginationItem
                              disabled={currentPage >= paginationInfo.total_pages}
                            >
                              <PaginationLink
                                next
                                onClick={() => handlePageChange(currentPage + 1)}
                              />
                            </PaginationItem>
                            <PaginationItem
                              disabled={currentPage >= paginationInfo.total_pages}
                            >
                              <PaginationLink
                                last
                                onClick={() => handlePageChange(paginationInfo.total_pages)}
                              />
                            </PaginationItem>
                          </Pagination>
                        )}
                      </div>
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Assign to Unit Modal */}
      <Modal
        isOpen={isAssignModalOpen}
        toggle={() => setIsAssignModalOpen(false)}
        centered
        size="lg"
      >
        <ModalHeader toggle={() => setIsAssignModalOpen(false)}>
          <i className="bx bx-link-alt text-primary me-1"></i> Assign Question #
          {questionToAssign?.question_id} to Unit
        </ModalHeader>
        <ModalBody>
          {questionToAssign && (
            <form onSubmit={handleAssignSubmit}>
              <div className="p-3 bg-light rounded mb-3">
                <label className="form-label fw-bold text-muted mb-1">
                  Question Text:
                </label>
                <div className="fw-semibold text-dark">
                  {questionToAssign.question_text}
                </div>
              </div>

              {/* Course Selector */}
              <div className="mb-3">
                <label className="form-label fw-bold">
                  Select Target Course <span className="text-danger">*</span>
                </label>
                <Select
                  placeholder="Search and select course..."
                  options={courses}
                  isLoading={loadingCourses}
                  value={selectedCourse}
                  onChange={(selected) => {
                    setSelectedCourse(selected);
                    if (selected) {
                      getUnits(selected.value);
                    } else {
                      setUnits([]);
                      setSelectedUnit(null);
                    }
                  }}
                  isClearable
                />
              </div>

              {/* Unit Selector */}
              <div className="mb-4">
                <label className="form-label fw-bold">
                  Select Target Unit <span className="text-danger">*</span>
                </label>
                <Select
                  placeholder={
                    !selectedCourse
                      ? "Please select a course first..."
                      : "Search and select unit..."
                  }
                  options={units}
                  isLoading={loadingUnits}
                  value={selectedUnit}
                  isDisabled={!selectedCourse}
                  onChange={(selected) => setSelectedUnit(selected)}
                  isClearable
                />
              </div>

              <div className="d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setIsAssignModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-success"
                  disabled={!selectedCourse || !selectedUnit || isAssigning}
                >
                  {isAssigning ? (
                    <>
                      <Spinner size="sm" className="me-1" /> Assigning...
                    </>
                  ) : (
                    <>
                      <i className="bx bx-check me-1"></i> Confirm & Assign
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </ModalBody>
      </Modal>

      {/* Edit Question Modal */}
      <Modal
        isOpen={isEditModalOpen}
        toggle={() => setIsEditModalOpen(false)}
        size="lg"
        centered
      >
        <ModalHeader toggle={() => setIsEditModalOpen(false)}>
          <i className="bx bx-edit text-primary me-1"></i> Edit Question #
          {editQuestionData.question_id}
        </ModalHeader>
        <ModalBody>
          <form onSubmit={handleEditQuestionSubmit}>
            <div className="mb-3">
              <label className="form-label fw-bold">Question Title *</label>
              <textarea
                className="form-control"
                rows="3"
                placeholder="Enter question title..."
                value={editQuestionData.question_text || ""}
                onChange={(e) =>
                  setEditQuestionData({
                    ...editQuestionData,
                    question_text: e.target.value,
                  })
                }
                required
              ></textarea>
            </div>

            <div className="mb-3">
              <label className="form-label fw-bold">Question Image (Optional)</label>
              <div className="d-flex gap-2 align-items-center">
                <input
                  type="file"
                  className="form-control"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) handleUploadImage(file, true);
                  }}
                />
                {uploadingEditImg && <Spinner size="sm" color="primary" />}
              </div>
              {editQuestionData.question_image_url && (
                <div className="mt-2 d-flex align-items-center gap-2">
                  <img
                    src={editQuestionData.question_image_url}
                    alt="preview"
                    style={{
                      maxHeight: "120px",
                      borderRadius: "6px",
                      border: "1px solid #ddd",
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() =>
                      setEditQuestionData({
                        ...editQuestionData,
                        question_image_url: "",
                      })
                    }
                  >
                    Remove Image
                  </button>
                </div>
              )}
            </div>

            {/* Answers Section */}
            <div className="mb-3 p-3 bg-light rounded">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <label className="form-label fw-bold mb-0">
                  Answer Options (Select correct answer using radio) *
                </label>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary"
                  onClick={() =>
                    setEditAnswersList([
                      ...editAnswersList,
                      {
                        id: editAnswersList.length + 1,
                        answer: "",
                        answer_exp: "",
                        checked: false,
                      },
                    ])
                  }
                >
                  <i className="bx bx-plus me-1"></i> Add Option
                </button>
              </div>

              {editAnswersList.map((item, index) => (
                <div
                  key={index}
                  className="p-2 mb-2 bg-white rounded border d-flex flex-column gap-2"
                >
                  <div className="d-flex align-items-center gap-2">
                    <input
                      type="radio"
                      name="correct_answer_edit"
                      checked={item.checked}
                      onChange={() => {
                        const list = editAnswersList.map((a, idx) => ({
                          ...a,
                          checked: idx === index,
                        }));
                        setEditAnswersList(list);
                      }}
                      style={{ cursor: "pointer", transform: "scale(1.2)" }}
                    />
                    <Input
                      type="text"
                      placeholder={`Answer ${index + 1}`}
                      value={item.answer}
                      onChange={(e) => {
                        const list = [...editAnswersList];
                        list[index].answer = e.target.value;
                        setEditAnswersList(list);
                      }}
                      required
                    />
                    {editAnswersList.length > 2 && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => {
                          const list = editAnswersList.filter(
                            (_, i) => i !== index
                          );
                          if (item.checked && list.length > 0) {
                            list[0].checked = true;
                          }
                          setEditAnswersList(list);
                        }}
                      >
                        <i className="bx bx-trash"></i>
                      </button>
                    )}
                  </div>
                  <Input
                    type="text"
                    placeholder={`Explanation for answer ${index + 1} (Optional)`}
                    value={item.answer_exp || ""}
                    onChange={(e) => {
                      const list = [...editAnswersList];
                      list[index].answer_exp = e.target.value;
                      setEditAnswersList(list);
                    }}
                    className="font-size-12 text-muted"
                  />
                </div>
              ))}
            </div>

            <div className="mb-3">
              <label className="form-label">General Explanation (Optional)</label>
              <textarea
                className="form-control"
                rows="2"
                placeholder="Enter explanation for the correct answer..."
                value={editQuestionData.explain || ""}
                onChange={(e) =>
                  setEditQuestionData({
                    ...editQuestionData,
                    explain: e.target.value,
                  })
                }
              ></textarea>
            </div>

            <div className="d-flex justify-content-end gap-2 mt-4">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setIsEditModalOpen(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={submittingEdit || uploadingEditImg}
              >
                {submittingEdit ? (
                  <>
                    <Spinner size="sm" className="me-1" /> Updating...
                  </>
                ) : (
                  "Update Question"
                )}
              </button>
            </div>
          </form>
        </ModalBody>
      </Modal>

      <ToastContainer />
    </React.Fragment>
  );
};

export default AddQuestions;
